import { TFunction } from 'react-i18next';
import type { DataGridV2Tone } from '@bloomays-lib/ui.shared/datagrid';
import {
  ContractLegalStatus,
  ContractsByMissionGQL,
  LegalContractGQL,
  MissionDataForGetLegalContractsResult,
  ResolvedContact,
  TemplateContract,
} from '@bloomays-lib/types.shared';
import { deriveContractLateStatus, computeMissionContractCounts } from '@bloomays-lib/utils.shared';
import groupBy from 'lodash/fp/groupBy';
import map from 'lodash/fp/map';
import flatMap from 'lodash/fp/flatMap';
import sortBy from 'lodash/fp/sortBy';
import uniqBy from 'lodash/fp/uniqBy';

/**
 * View-model for the "Contrats et avenants" mission-grouped screen (TEC-3160).
 *
 * The GraphQL payload (`ContractsByMissionGQL[]`) returns one `LegalContractGQL`
 * per (step × volet) — e.g. "Contrat principal" SST and "Contrat principal" PDS
 * are two separate entries. The maquette expects a single documentary line per
 * step with two distinct volet zones, so this module pivots the flat payload
 * into `MissionContractBlock[]` without any backend change.
 */

/** A single volet (SST or PDS) of a documentary line, with its resolved badge. */
export type DocumentVolet = {
  contract: LegalContractGQL;
  badgeLabel: string;
  badgeTone: DataGridV2Tone;
  /**
   * Statut dérivé « en retard » (contrat principal non signé + mission démarrée). Toujours `false`
   * pour un avenant. Dérivé via `deriveContractLateStatus` (règle partagée front/back, spec 004).
   */
  isLate: boolean;
};

/** A documentary line (contrat principal or a numbered avenant) within a mission. */
export type ContractDocumentLine = {
  step: string;
  stepOrder: number;
  sst: DocumentVolet | null;
  pds: DocumentVolet | null;
};

/** A mission and its documentary lines, ready to be grouped/rendered by DataGridV2. */
export type MissionContractBlock = {
  missionId: string;
  missionLabel: string;
  bloomerFullName: string;
  clientSocietyName: string;
  bloomerSocietyName: string | null;
  accountManagerName: string | null;
  /** Display name of the resolved SST signer (supplier legal contact / bloomer). */
  sstSignerFullName: string | null;
  /** Display name of the resolved PDS signer (client legal / ops contact). */
  pdsSignerFullName: string | null;
  lines: ContractDocumentLine[];
  /** Documents « on going » (principal + avenants), dérivé côté front. */
  toSignCount: number;
  /** Contrats principaux en retard, dérivé côté front (`computeMissionContractCounts`). */
  lateCount: number;
};

/** A `ContractDocumentLine` flattened with its mission fields, one per DataGridV2 row. */
export type MissionContractGridRow = ContractDocumentLine & {
  rowId: string;
  missionId: string;
  missionLabel: string;
  bloomerFullName: string;
  clientSocietyName: string;
  bloomerSocietyName: string | null;
  accountManagerName: string | null;
  sstSignerFullName: string | null;
  pdsSignerFullName: string | null;
};

/** Builds a display name from a resolved legal contact (fullname preferred). */
export const resolveSignerDisplayName = (resolved: ResolvedContact | null | undefined): string | null => {
  const contact = resolved?.contact;
  if (!contact) return null;
  if (contact.fullname?.trim()) return contact.fullname.trim();
  const composed = `${contact.firstname ?? ''} ${contact.lastname ?? ''}`.trim();
  return composed || null;
};

export const getAccountManagerName = (
  accountManager?: MissionDataForGetLegalContractsResult['accountManager'],
): string => {
  const firstname = accountManager?.firstname ?? '';
  const lastname = accountManager?.lastname ?? '';
  return `${firstname} ${lastname}`.trim();
};

/**
 * Maps the 5 backend `ContractLegalStatus` values to a badge label/tone.
 *
 * `pending` means the amendment document failed to *generate* (not "en attente
 * de signature", which is `on going`) — see research.md R4. The exhaustive
 * `switch` (no `default` fallback) makes the TS compiler fail if a new status
 * value is introduced without an explicit mapping decision.
 */
export const mapContractStatusToBadge = (
  status: ContractLegalStatus,
  t: TFunction<'legalContracts'[], undefined>,
): { label: string; tone: DataGridV2Tone } => {
  switch (status) {
    case 'on going':
      return { label: t('badge.toSign'), tone: 'warning' };
    case 'done':
      return { label: t('badge.signed'), tone: 'success' };
    case 'cancelled':
      return { label: t('badge.cancelled'), tone: 'neutral' };
    case 'refused':
      return { label: t('badge.refused'), tone: 'danger' };
    case 'pending':
      // Distinct tone from `refused` (both otherwise "danger") so a generation
      // error can't be visually mistaken for a business refusal (FR-011).
      return { label: t('badge.generationError'), tone: 'purple' };
  }
};

/**
 * Extracts the sort rank from a `step` label ("Contrat principal" → 0,
 * "Avenant N°1" → 1, "Avenant N°10" → 10). Falls back to 0 (main-contract rank)
 * for any unrecognized label so an unexpected step never crashes the ordering.
 */
export const extractStepOrder = (step: string): number => {
  const match = step.match(/(\d+)/);
  return match ? Number(match[1]) : 0;
};

const toDocumentVolet = (
  contract: LegalContractGQL,
  t: TFunction<'legalContracts'[], undefined>,
  beginMission?: string | null,
): DocumentVolet => {
  const { label, tone } = mapContractStatusToBadge(contract.status, t);
  return { contract, badgeLabel: label, badgeTone: tone, isLate: deriveContractLateStatus(contract, beginMission) };
};

/**
 * Pivots the flat `ContractsByMissionGQL[]` payload into mission blocks whose
 * lines already carry a distinct SST/PDS volet and are ordered contrat
 * principal → avenants croissants.
 */
export const buildMissionContractGroups = (
  missionsContracts: ContractsByMissionGQL[],
  t: TFunction<'legalContracts'[], undefined>,
): MissionContractBlock[] =>
  map((contractByMission: ContractsByMissionGQL): MissionContractBlock => {
    const { mission, contracts: rawContracts } = contractByMission;
    const contracts = rawContracts ?? [];
    const contractsByStep = groupBy((contract: LegalContractGQL) => contract.step, contracts);

    const lines: ContractDocumentLine[] = map((step: string): ContractDocumentLine => {
      const contractsForStep = contractsByStep[step] ?? [];
      const sstContract = contractsForStep.find((contract) => contract.type === TemplateContract.SST) ?? null;
      const pdsContract = contractsForStep.find((contract) => contract.type === TemplateContract.PDS) ?? null;

      return {
        step,
        stepOrder: extractStepOrder(step),
        sst: sstContract ? toDocumentVolet(sstContract, t, mission.beginMission) : null,
        pds: pdsContract ? toDocumentVolet(pdsContract, t, mission.beginMission) : null,
      };
    }, Object.keys(contractsByStep));

    const sortedLines = sortBy((line: ContractDocumentLine) => line.stepOrder, lines);
    const { toSign, late } = computeMissionContractCounts(contracts, mission.beginMission);

    return {
      missionId: mission.id,
      missionLabel: mission.label,
      bloomerFullName: mission.bloomerFullName,
      clientSocietyName: mission.clientSocietyName,
      bloomerSocietyName: mission.bloomerSocietyName,
      accountManagerName: getAccountManagerName(mission.accountManager) || null,
      sstSignerFullName: resolveSignerDisplayName(mission.resolvedSupplierSstContact),
      pdsSignerFullName: resolveSignerDisplayName(mission.resolvedClientPdsContact),
      lines: sortedLines,
      toSignCount: toSign,
      lateCount: late,
    };
  }, missionsContracts);

/** A dropdown option for a quick filter (label/value pair). */
export type FilterOption = { value: string; label: string };

/** Distinct, alphabetically-sorted AM options derived from already-fetched rows, for the "Tous les AM" quick filter. */
export const buildAccountManagerOptions = (rows: MissionContractGridRow[]): FilterOption[] =>
  sortBy(
    (option: FilterOption) => option.label.toLocaleLowerCase('fr'),
    uniqBy(
      'value',
      rows
        .filter((row): row is MissionContractGridRow & { accountManagerName: string } => !!row.accountManagerName)
        .map((row) => ({ value: row.accountManagerName, label: row.accountManagerName })),
    ),
  );

/** Valeur du quick-filter « À signer / Signés » (`''` = tous). */
export type SigningStateFilter = 'toSign' | 'signed' | '';

export const isVoletVisibleUnderSigningFilter = (volet: DocumentVolet | null, filter: SigningStateFilter): boolean => {
  if (!filter) return true;
  if (!volet) return false;
  return filter === 'toSign' ? volet.contract.status === 'on going' : volet.contract.status === 'done';
};

/** Flattens mission blocks into one grid row per documentary line, for `DataGridV2`. */
export const flattenMissionContractGroups = (blocks: MissionContractBlock[]): MissionContractGridRow[] =>
  flatMap(
    (block: MissionContractBlock): MissionContractGridRow[] =>
      map(
        (line: ContractDocumentLine): MissionContractGridRow => ({
          ...line,
          rowId: `${block.missionId}-${line.step}`,
          missionId: block.missionId,
          missionLabel: block.missionLabel,
          bloomerFullName: block.bloomerFullName,
          clientSocietyName: block.clientSocietyName,
          bloomerSocietyName: block.bloomerSocietyName,
          accountManagerName: block.accountManagerName,
          sstSignerFullName: block.sstSignerFullName,
          pdsSignerFullName: block.pdsSignerFullName,
        }),
        block.lines,
      ),
    blocks,
  );
